import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Shield, AlertTriangle, CheckCircle, Copy, Plus, Trash2, Upload, Activity, Lock, Globe, X } from 'lucide-react';
import { getDeliverabilityHealth, getDNSRecords, getSuppressionList, addToSuppression, removeFromSuppression, importSuppression } from '../lib/api';

export default function DeliverabilityPage() {
  const [health, setHealth] = useState(null);
  const [dnsRecords, setDNSRecords] = useState(null);
  const [suppressionList, setSuppressionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSuppressEmail, setNewSuppressEmail] = useState('');
  const [showAddSuppress, setShowAddSuppress] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [healthRes, dnsRes, supRes] = await Promise.all([
        getDeliverabilityHealth(),
        getDNSRecords(),
        getSuppressionList(),
      ]);
      setHealth(healthRes.data);
      setDNSRecords(dnsRes.data);
      setSuppressionList(supRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuppression = async () => {
    if (!newSuppressEmail.trim()) return;
    try {
      await addToSuppression({ email: newSuppressEmail, reason: 'manual' });
      toast.success('Email added to suppression list');
      setNewSuppressEmail('');
      setShowAddSuppress(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleRemoveSuppression = async (email) => {
    try {
      await removeFromSuppression(email);
      toast.success('Email removed from suppression list');
      loadData();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleImportSuppression = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await importSuppression(file);
      toast.success(`Imported ${res.data.imported} emails to suppression list`);
      loadData();
    } catch (err) {
      toast.error('Import failed');
    }
    e.target.value = '';
  };

  const healthColor = (status) => {
    if (status === 'good') return 'hsl(var(--status-success))';
    if (status === 'warning') return 'hsl(var(--status-warning))';
    return 'hsl(var(--status-danger))';
  };

  if (loading) return <div className="page-container"><p>Loading...</p></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Deliverability</h1>
        <p className="page-subtitle">Monitor email health, manage suppression list, and configure DNS records</p>
      </div>

      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health">Health Score</TabsTrigger>
          <TabsTrigger value="suppression">Suppression List ({suppressionList.length})</TabsTrigger>
          <TabsTrigger value="dns">DNS Records</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="space-y-4 mt-4">
          {health && (
            <>
              {/* Health Score */}
              <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Deliverability Score</h3>
                      <p className="text-5xl font-bold mt-1" style={{fontFamily: 'Space Grotesk', color: healthColor(health.status)}} data-testid="health-score">
                        {health.health_score}
                      </p>
                    </div>
                    <Badge className={`text-sm px-3 py-1 ${
                      health.status === 'good' ? 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))]' :
                      health.status === 'warning' ? 'bg-[hsl(var(--status-warning)/0.15)] text-[hsl(var(--status-warning))]' :
                      'bg-[hsl(var(--status-danger)/0.15)] text-[hsl(var(--status-danger))]'
                    }`} data-testid="health-status">
                      {health.status}
                    </Badge>
                  </div>
                  <Progress value={health.health_score} className="h-2" />
                </CardContent>
              </Card>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Total Sent', value: health.total_sent, icon: Activity },
                  { label: 'Failed', value: health.total_failed, icon: AlertTriangle },
                  { label: 'Bounce Rate', value: `${health.bounce_rate}%`, icon: X },
                  { label: 'Open Rate', value: `${health.open_rate}%`, icon: CheckCircle },
                  { label: 'Suppressed', value: health.suppressed_emails, icon: Shield },
                ].map((m, i) => (
                  <Card key={i} className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                    <CardContent className="p-4 text-center">
                      <m.icon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xl font-semibold" style={{fontFamily: 'Space Grotesk'}}>{m.value}</p>
                      <p className="text-[10px] text-muted-foreground">{m.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="suppression" className="space-y-4 mt-4">
          <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Suppression List</CardTitle>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv" className="hidden" onChange={handleImportSuppression} />
                    <Button variant="outline" size="sm" asChild data-testid="import-suppression-btn">
                      <span><Upload className="w-4 h-4 mr-1" /> Import CSV</span>
                    </Button>
                  </label>
                  <Dialog open={showAddSuppress} onOpenChange={setShowAddSuppress}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[hsl(var(--accent))] text-white" data-testid="add-suppression-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Email
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Suppress Email</DialogTitle><DialogDescription>Add an email to prevent sending</DialogDescription></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Email Address</Label>
                          <Input value={newSuppressEmail} onChange={(e) => setNewSuppressEmail(e.target.value)}
                            placeholder="email@example.com" data-testid="suppress-email-input" />
                        </div>
                        <Button onClick={handleAddSuppression} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="confirm-suppress-btn">
                          Suppress Email
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {suppressionList.length > 0 ? (
                <Table data-testid="suppression-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppressionList.map((s, i) => (
                      <TableRow key={i} className="table-row-hover">
                        <TableCell className="font-mono text-sm">{s.email}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            s.reason === 'bounce' ? 'bg-[hsl(var(--status-danger)/0.15)] text-[hsl(var(--status-danger))]' :
                            s.reason === 'complaint' ? 'bg-[hsl(var(--status-warning)/0.15)] text-[hsl(var(--status-warning))]' :
                            'bg-secondary text-foreground'
                          }`}>{s.reason}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveSuppression(s.email)}>
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Shield className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No suppressed emails</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dns" className="space-y-4 mt-4">
          {dnsRecords && (
            <>
              <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <CardTitle className="text-sm font-medium">DNS Records for {dnsRecords.domain}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {dnsRecords.records.map((r, i) => (
                    <div key={i} className="p-3 bg-secondary/20 rounded-lg border border-border space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{r.type}</Badge>
                          <span className="text-sm font-medium">{r.purpose}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(r.value); toast.success('Copied!'); }}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Name: <span className="font-mono">{r.name}</span></p>
                        <p className="text-xs font-mono bg-secondary/40 p-2 rounded mt-1 break-all">{r.value}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Deliverability Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {dnsRecords.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[hsl(var(--status-success))] flex-shrink-0 mt-0.5" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
